import React, { FC, ReactNode, useEffect } from 'react'

// Third party
import clsx from 'clsx'
import { v4 } from 'uuid'
import { Tooltip } from '@mui/material'

// Redux
import { useAppSelector } from '../../../../store/hooks'
import { selectRegistrationR } from '../../../../store/exhibitor/exhibitorSlice'

import TooltipIcon from '../../../../helpers/TooltipIcon'

import {
  HORSE_NAME_DEFAULT,
  IRegistrationByDayInterface,
} from '../../../../models/registrations-by-day/registrationByDay.interface'
import { IHorseData } from '../../../../models/horse/horse.interface'
import { PAYMENT_STATUS } from '../../../../types/payment'
import { getRiderHorses } from '../../../../helpers/riders'
import { getFloatPrice } from '../../../../helpers/price'

type IExhibitorRegistrationAccordionProps = {
  registrationsByDay: IRegistrationByDayInterface[]
  registrationPrice: number
  showScratchedView: boolean
  setListToScratch: React.Dispatch<React.SetStateAction<string[]>>
  listToScratch: string[]
  horses: IHorseData[]
  currency: string
}

type IDetailRowProps = {
  title: string
  cost?: number | null
  showScratchedView: boolean
  listToScratch: string[]
  member: IRegistrationByDayInterface
  handleScratchList: (memberId: string) => void
  horses: IHorseData[]
  horseName: string | null
  currency: string
}

const ExhibitorRegistrationInput: FC<{
  className?: string
  data: ReactNode
  data1?: ReactNode
  icon?: string
}> = ({ className, icon, data }) => {
  return (
    <div className={className}>
      {icon ? (
        <img className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2" src={icon} alt="icon" />
      ) : null}
      <div className={clsx('max-w-full', icon && 'pl-6')}>{data}</div>
    </div>
  )
}

const DetailRow = (props: IDetailRowProps) => {
  let id = `${v4()}${Date.now()}`
  let { title, cost, member, showScratchedView, listToScratch, currency } = props
  let checked = member.id ? listToScratch.find((data) => data === member.id) : false

  if (isNaN(Number(cost))) return null

  return (
    <div className="border rounded-md shadow-sm border-[#D3DAEE] lg:border-0 lg:shadow-none p-2 lg:p-0 mt-4 lg:mt-0 flex flex-col lg:flex-row w-full">
      <div className="lg:hidden font-medium text-[12px]">Class</div>
      <ExhibitorRegistrationInput
        className="rounded-lg flex items-center text-sm text-SeabiscuitDark200ThemeColor min-h-[48px] bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4"
        data={
          <div className="flex flex-wrap items-center gap-1 w-full">
            {title}
            <div className="ml-auto text-[10px] text-SeabiscuitDark200ThemeColor flex items-center gap-2">
              {member.isScratched && (
                <span className="border border-SeabiscuitDark200ThemeColor  px-2 py-0.5 rounded-full">
                  Scratched
                </span>
              )}
              {member.isQualify && (
                <span className="border border-SeabiscuitDark200ThemeColor px-2 py-0.5 rounded-full">
                  Bonus Fee
                </span>
              )}
            </div>
          </div>
        }
      />

      <div className="lg:hidden mt-2 font-medium text-[12px]">Horse</div>
      <ExhibitorRegistrationInput
        className="rounded-lg flex items-center justify-center text-sm text-SeabiscuitDark200ThemeColor bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4 text-center relative"
        data={
          props.horses?.[0]
            ? props.horses.map((item) => item.horseName).join(', ')
            : HORSE_NAME_DEFAULT
        }
        icon={'/assets/og_icons/YearofHorse-1.svg'}
      />

      <div className="lg:hidden mt-2 font-medium text-[12px]">Rider</div>
      <ExhibitorRegistrationInput
        className="rounded-lg flex items-center justify-center text-sm text-SeabiscuitDark200ThemeColor bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4 text-center relative"
        data={member ? member.riderName || member.riderEmail : 'N/A'}
      />

      <div className="lg:hidden mt-2 font-medium text-[12px]">Price</div>
      <ExhibitorRegistrationInput
        className="relative rounded-lg flex items-center justify-center text-sm text-SeabiscuitDark200ThemeColor bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4 text-center"
        data={
          member?.isScratched ? (
            'Scratched'
          ) : (
            <span>
              {getFloatPrice(`${currency}${cost}`)}{' '}
              {member.isQualify && (
                <span>
                  • {getFloatPrice(`${currency}${member.qualifyFee}`)}{' '}
                  {member.qualifyFeeName || 'qual'}
                </span>
              )}
            </span>
          )
        }
        icon={
          member.paymentStatus === PAYMENT_STATUS.PAID
            ? '/assets/img/dark/credit-card.svg'
            : '/assets/img/dark/cash.svg'
        }
      />

      {showScratchedView ? (
        <label
          htmlFor={id}
          className={clsx(
            'rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor bg-transparent border border-[#d3daee] p-3 m-1 w-1/4 text-center relative flex items-center justify-center',
            !member?.isScratched && 'cursor-pointer'
          )}
        >
          {member?.isScratched ? (
            'Scratched'
          ) : (
            <>
              Scratch
              <div
                className={clsx(
                  'ml-2 !ring-0 !shadow-none absolute shrink-0 flex items-center justify-center',
                  !checked
                    ? 'border-2 border-SeabiscuitDark200ThemeColor rounded-full w-5 h-5 right-[14px]'
                    : 'w-6 h-6 right-3'
                )}
              >
                {checked ? (
                  <img
                    src="/assets/og_icons/Ok-3.svg"
                    alt="Ok"
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>
              <input
                type="checkbox"
                className="hidden"
                id={id}
                checked={!!checked}
                onChange={() => props.handleScratchList(member?.id ? member?.id.toString() : '')}
              />
            </>
          )}
        </label>
      ) : null}
    </div>
  )
}

const ExhibitorRegistrationAccordion = ({
  registrationsByDay,
  showScratchedView,
  setListToScratch,
  listToScratch,
  horses,
  currency,
}: IExhibitorRegistrationAccordionProps) => {
  const registrationR = useAppSelector(selectRegistrationR)

  useEffect(() => {
    setListToScratch([])
  }, [showScratchedView, registrationR])

  const handleScratchList = (memberId: string) => {
    if (listToScratch.find((data) => data === memberId)) {
      let filter = listToScratch.filter((data) => data !== memberId)
      setListToScratch(filter)
    } else {
      setListToScratch([...listToScratch, memberId])
    }
  }
  return (
    <>
      <div className="hidden lg:flex items-center w-full">
        <ExhibitorRegistrationInput
          className={clsx(
            'xl:text-sm 2xl:text-base text-SeabiscuitDark200ThemeColor font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center text-xs">Class</span>}
        />

        <ExhibitorRegistrationInput
          className={clsx(
            'xl:text-sm 2xl:text-base text-SeabiscuitDark200ThemeColor font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center text-xs">Horse</span>}
        />

        <ExhibitorRegistrationInput
          className={clsx(
            'xl:text-sm 2xl:text-base text-SeabiscuitDark200ThemeColor font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center text-xs">Rider</span>}
        />

        <ExhibitorRegistrationInput
          className={clsx(
            'xl:text-sm 2xl:text-base text-SeabiscuitDark200ThemeColor font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center text-xs">Price</span>}
        />

        {showScratchedView ? (
          <ExhibitorRegistrationInput
            className={clsx(
              'xl:text-sm 2xl:text-base text-SeabiscuitDark200ThemeColor font-semibold w-1/5'
            )}
            data={
              <span className="flex items-center text-xs">
                Scratch
                <Tooltip title={<h1 className="tooltip_title">Scratch</h1>} placement="top" arrow>
                  <button className="">
                    <TooltipIcon color="#122B46" />
                  </button>
                </Tooltip>
              </span>
            }
          />
        ) : null}
      </div>

      {registrationsByDay && registrationsByDay.length
        ? registrationsByDay.map((currentItem, index) => {
            return (
              <DetailRow
                key={index}
                title={currentItem.registrationByDayName ?? 'N/A'}
                listToScratch={listToScratch}
                member={currentItem}
                horses={getRiderHorses(horses, currentItem?.horsesSelected || [])}
                handleScratchList={handleScratchList}
                showScratchedView={showScratchedView}
                cost={currentItem.registrationPrice}
                horseName={currentItem.horseName || null}
                currency={currency}
              />
            )
          })
        : null}
    </>
  )
}

export default ExhibitorRegistrationAccordion
